import { useDeferredValue, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { ArrowLeft } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Spinner } from '@/components/ui/spinner'
import { workersApi } from '@/services/workers'
import { categoriesApi } from '@/services/categories'
import { SearchableSelect } from '@/components/common/searchable-select'
import { orgUsersApi } from '@/services/org-users'
import { getErrorMessage } from '@/lib/api'
import { getServiceInstanceIdFromSearch, withServiceInstanceId } from '@/lib/service-instance'
import { resolveReturnToPath } from '@/lib/return-navigation'
import { buildMapSearchDetails, getMapSearchAddress } from '@/lib/map-location'

const workerSchema = z.object({
  display_name: z.string().trim().min(1, 'Display name is required'),
  worker_code: z.string().trim().regex(/^[1-9][0-9]{5}$/, 'Worker ID must be 6 digits and cannot start with 0'),
  phone: z.string().trim().optional(),
  email: z.string().trim().email('Enter a valid email').optional().or(z.literal('')),
  address: z.string().trim().optional(),
  permanent_latitude: z.string().trim().optional(),
  permanent_longitude: z.string().trim().optional(),
  permanent_location_text: z.string().trim().optional(),
  linked_address_name: z.string().trim().optional(),
  linked_address_door_no: z.string().trim().optional(),
  linked_address_building_name: z.string().trim().optional(),
  linked_address_floor_line: z.string().trim().optional(),
  linked_address_street_line: z.string().trim().optional(),
  linked_address_city_name: z.string().trim().optional(),
  linked_address_zipcode: z.string().trim().optional(),
  linked_address_mobile_number: z.string().trim().optional(),
  linked_address_email: z.string().trim().email('Enter a valid linked address email').optional().or(z.literal('')),
  verified: z.boolean().default(false),
  categories: z.array(z.string()).optional(),
})

type WorkerFormData = z.infer<typeof workerSchema>

const normalizeCategoryId = (categoryId: string | number) => String(categoryId)

const toggleCategory = (
  current: string[] | undefined,
  categoryId: string,
  checked: boolean,
) => {
  const normalizedId = normalizeCategoryId(categoryId)
  const values = (current ?? []).map(normalizeCategoryId)
  if (checked) {
    return values.includes(normalizedId) ? values : [...values, normalizedId]
  }
  return values.filter((value) => value !== normalizedId)
}

const ReadOnlyField = ({ label, value }: { label: string; value?: string | null }) => (
  <div className="space-y-1">
    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className="text-sm font-medium text-foreground">{value?.trim() || '—'}</p>
  </div>
)

type PermanentLocationSearchOption = {
  id: string
  label: string
  lat: string
  lon: string
  address: string
  details: string
}

const toCoordinateString = (value?: string | number | null) => {
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric.toFixed(6) : ''
}

const toNullableCoordinate = (value?: string | number | null) => {
  const numeric = Number(value)
  return Number.isFinite(numeric) ? Number(numeric.toFixed(6)) : null
}

export default function WorkerEditPage() {
  const { id } = useParams<{ id: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)
  const workerFallbackPath = withServiceInstanceId(`/workers/${id}`, serviceInstanceId)
  const returnPath = resolveReturnToPath(location.search, workerFallbackPath)
  const [permanentLocationSearch, setPermanentLocationSearch] = useState('')
  const [permanentLocationOptions, setPermanentLocationOptions] = useState<PermanentLocationSearchOption[]>([])
  const [isSearchingPermanentLocation, setIsSearchingPermanentLocation] = useState(false)
  const [workerType, setWorkerType] = useState<number>(1)
  const [contractorUserId, setContractorUserId] = useState('')
  const [contractorSearch, setContractorSearch] = useState('')
  const deferredContractorSearch = useDeferredValue(contractorSearch.trim())

  const workerQuery = useQuery({
    queryKey: ['worker', id],
    queryFn: () => workersApi.getWorker(id!),
    enabled: !!id,
  })

  const effectiveServiceInstanceId = useMemo(() => {
    if (serviceInstanceId) {
      return serviceInstanceId
    }

    const worker = workerQuery.data as Record<string, unknown> | undefined
    const candidate = worker?.service_instance_id ?? worker?.service_instance ?? worker?.org_instance_id ?? worker?.org_instance
    const normalized = String(candidate ?? '').trim()
    return normalized || ''
  }, [serviceInstanceId, workerQuery.data])

  const categoriesQuery = useQuery({
    queryKey: ['job-categories', 'worker-edit', serviceInstanceId ?? 'none'],
    queryFn: () => categoriesApi.getCategories(),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  })
  const categories = categoriesQuery.data?.results ?? []

  const contractorUsersQuery = useQuery({
    queryKey: ['org-users', 'worker-edit', 'contractors', effectiveServiceInstanceId || 'none', deferredContractorSearch || ''],
    queryFn: () => orgUsersApi.list({
      serviceInstanceId: effectiveServiceInstanceId || undefined,
      userGroupCode: 'contractor',
      search: deferredContractorSearch || undefined,
      orgUser: true,
      includeInactive: false,
      pageSize: 200,
    }),
    enabled: Boolean(effectiveServiceInstanceId) && workerType === 2,
    staleTime: 30_000,
  })

  const {
    control,
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<WorkerFormData>({
    resolver: zodResolver(workerSchema),
    defaultValues: {
      verified: false,
      categories: [],
    },
  })

  const permanentAddressValue = watch('address') || watch('permanent_location_text') || ''

  useEffect(() => {
    const worker = workerQuery.data
    if (!worker) {
      return
    }

    reset({
      display_name: worker.display_name || worker.full_name || '',
      worker_code: worker.worker_code || '',
      phone: worker.phone || worker.phone_number || '',
      email: worker.email || '',
      address: worker.address || '',
      permanent_latitude: toCoordinateString(worker.permanent_latitude),
      permanent_longitude: toCoordinateString(worker.permanent_longitude),
      permanent_location_text: worker.permanent_location_text || worker.address || '',
      linked_address_name: worker.linked_user_address?.name || worker.display_name || worker.full_name || '',
      linked_address_door_no: worker.linked_user_address?.door_no || '',
      linked_address_building_name: worker.linked_user_address?.building_name || '',
      linked_address_floor_line: worker.linked_user_address?.floor_line || '',
      linked_address_street_line: worker.linked_user_address?.street_line || worker.address || worker.permanent_location_text || '',
      linked_address_city_name: worker.linked_user_address?.city_name || '',
      linked_address_zipcode: worker.linked_user_address?.zipcode || '',
      linked_address_mobile_number: worker.linked_user_address?.mobile_number || worker.phone || worker.phone_number || '',
      linked_address_email: worker.linked_user_address?.email || worker.email || '',
      verified: worker.verified ?? Boolean(worker.is_approved),
      categories: (worker.categories ?? []).map(String),
    })
    setWorkerType(Number(worker.worker_type ?? 1))
    setContractorUserId(String(worker.contractor_user ?? ''))
    setContractorSearch('')
    setPermanentLocationSearch(worker.permanent_location_text || worker.address || '')
  }, [reset, workerQuery.data])

  useEffect(() => {
    if (!serviceInstanceId) {
      setPermanentLocationOptions([])
      setIsSearchingPermanentLocation(false)
      return
    }

    const query = permanentLocationSearch.trim()
    if (query.length < 3) {
      setPermanentLocationOptions([])
      setIsSearchingPermanentLocation(false)
      return
    }

    const timeoutId = window.setTimeout(async () => {
      try {
        setIsSearchingPermanentLocation(true)
        const payload = await workersApi.searchWorkerLocation(serviceInstanceId, query, 5)
        setPermanentLocationOptions(
          (payload.results ?? [])
            .map((entry) => {
              const address = getMapSearchAddress(entry)
              return {
                id: String(entry.id ?? '').trim(),
                label: String(entry.label ?? '').trim(),
                lat: String(entry.lat ?? '').trim(),
                lon: String(entry.lon ?? '').trim(),
                address,
                details: buildMapSearchDetails(entry),
              }
            })
            .filter((entry) => entry.id && entry.label && entry.lat && entry.lon),
        )
      } catch {
        setPermanentLocationOptions([])
      } finally {
        setIsSearchingPermanentLocation(false)
      }
    }, 350)

    return () => window.clearTimeout(timeoutId)
  }, [permanentLocationSearch, serviceInstanceId])

  const selectPermanentLocation = (option: PermanentLocationSearchOption) => {
    const latitude = toNullableCoordinate(option.lat)
    const longitude = toNullableCoordinate(option.lon)
    if (latitude === null || longitude === null) {
      return
    }

    const address = option.address || option.label
    setPermanentLocationSearch(address)
    setPermanentLocationOptions([])
    setValue('address', address, { shouldDirty: true, shouldValidate: true })
    setValue('permanent_location_text', address, { shouldDirty: true, shouldValidate: true })
    setValue('linked_address_street_line', address, { shouldDirty: true, shouldValidate: true })
    setValue('permanent_latitude', String(latitude), { shouldDirty: true, shouldValidate: true })
    setValue('permanent_longitude', String(longitude), { shouldDirty: true, shouldValidate: true })
  }

  const updateMutation = useMutation({
    mutationFn: (data: WorkerFormData) => workersApi.updateWorker(id!, {
      display_name: data.display_name,
      worker_code: data.worker_code,
      worker_type: workerType,
      contractor_user: workerType === 2 ? (contractorUserId || undefined) : undefined,
      phone: data.phone || undefined,
      email: data.email || undefined,
      address: data.address || undefined,
      permanent_latitude: data.permanent_latitude || undefined,
      permanent_longitude: data.permanent_longitude || undefined,
      permanent_location_text: data.permanent_location_text || data.address || undefined,
      linked_user_address: {
        name: data.linked_address_name || data.display_name || worker.display_name || worker.full_name || undefined,
        door_no: data.linked_address_door_no || undefined,
        building_name: data.linked_address_building_name || undefined,
        floor_line: data.linked_address_floor_line || undefined,
        street_line: data.linked_address_street_line || data.address || data.permanent_location_text || undefined,
        city_name: data.linked_address_city_name || undefined,
        zipcode: data.linked_address_zipcode || undefined,
        mobile_number: data.linked_address_mobile_number || data.phone || undefined,
        email: data.linked_address_email || data.email || undefined,
        latitude: data.permanent_latitude || undefined,
        longitude: data.permanent_longitude || undefined,
      },
      verified: data.verified,
      categories: data.categories ?? [],
    }),
    onSuccess: (worker) => {
      toast.success('Worker updated successfully')
      queryClient.invalidateQueries({ queryKey: ['worker', id] })
      queryClient.invalidateQueries({ queryKey: ['workers'] })
      navigate(resolveReturnToPath(location.search, withServiceInstanceId(`/workers/${worker.id}`, serviceInstanceId)))
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const onSubmit = (data: WorkerFormData) => {
    if (workerType === 2 && !contractorUserId) {
      toast.error('Select the contractor supplier account for external workers.')
      return
    }
    updateMutation.mutate(data)
  }

  const isVerified = watch('verified')
  const handleVerifiedChange = (checked: boolean) => {
    if (!checked) {
      setValue('verified', false, { shouldDirty: true, shouldValidate: true })
      return
    }

    const confirmationText = window.prompt('Type VERIFIED to confirm this worker as verified:')
    if (confirmationText?.trim().toUpperCase() !== 'VERIFIED') {
      toast.error('Verification confirmation failed. Worker remains unverified.')
      setValue('verified', false, { shouldDirty: true, shouldValidate: true })
      return
    }

    setValue('verified', true, { shouldDirty: true, shouldValidate: true })
  }

  if (workerQuery.isLoading) {
    return (
      <div className="flex min-h-[320px] items-center justify-center">
        <Spinner className="mr-2" />
        Loading worker...
      </div>
    )
  }

  if (workerQuery.isError || !workerQuery.data) {
    return (
      <div className="space-y-4">
        <p className="text-destructive">{workerQuery.isError ? getErrorMessage(workerQuery.error) : 'Worker not found'}</p>
        <Button type="button" variant="outline" onClick={() => navigate(returnPath)}>
          Back to workers
        </Button>
      </div>
    )
  }

  const worker = workerQuery.data

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(returnPath)}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-foreground">Edit Worker</h1>
          <p className="text-sm text-muted-foreground">Update contact details, categories, and worker state. Role and post assignment are read-only.</p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid gap-6 xl:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Worker profile</CardTitle>
              <CardDescription>Post holder and role details are read-only. Contact fields can be overridden here.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="display-name">Display name</Label>
                  <Input
                    id="display-name"
                    placeholder="Worker display name"
                    {...register('display_name')}
                    aria-invalid={!!errors.display_name}
                  />
                  {errors.display_name ? <p className="text-sm text-destructive">{errors.display_name.message}</p> : null}
                </div>
                <ReadOnlyField label="Role" value={worker.role_name} />
                <div className="space-y-2">
                  <Label htmlFor="worker-type">Worker type</Label>
                  <Select
                    value={String(workerType)}
                    onValueChange={(value) => {
                      const nextType = Number(value)
                      setWorkerType(nextType)
                      if (nextType !== 2) {
                        setContractorUserId('')
                      }
                    }}
                  >
                    <SelectTrigger id="worker-type">
                      <SelectValue placeholder="Select worker type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Internal</SelectItem>
                      <SelectItem value="2">External / Contract</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <ReadOnlyField label="Client" value={worker.client} />
                <ReadOnlyField label="Registration status" value={worker.registration_status_label || worker.status} />
              </div>

              {workerType === 2 ? (
                <div className="space-y-2">
                  <Label>Contractor supplier</Label>
                  <SearchableSelect
                    ariaLabel="Contractor supplier"
                    value={contractorUserId}
                    onValueChange={setContractorUserId}
                    onSearchChange={setContractorSearch}
                    options={(contractorUsersQuery.data ?? []).map((user) => ({
                      value: String(user.user_id),
                      label: `${String(user.fname ?? '').trim()} ${String(user.lname ?? '').trim()}`.trim() || String(user.username ?? user.user_id),
                      keywords: [user.username, user.email, user.phone, user.usr_reg_num].filter(Boolean).join(' '),
                    }))}
                    placeholder={contractorUsersQuery.isLoading ? 'Loading contractors…' : 'Select contractor supplier'}
                    searchPlaceholder="Search contractors…"
                    emptyText="No contractor users found."
                    filterLocally={false}
                    disabled={contractorUsersQuery.isLoading}
                  />
                </div>
              ) : null}

              <div className="space-y-2">
                <Label htmlFor="worker-code">Worker ID</Label>
                <Input
                  id="worker-code"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="Example: 123456"
                  {...register('worker_code')}
                  aria-invalid={!!errors.worker_code}
                />
                {errors.worker_code ? (
                  <p className="text-sm text-destructive">{errors.worker_code.message}</p>
                ) : (
                  <p className="text-xs text-muted-foreground">Use this 6-digit Worker ID as the User ID/PIN on ADMS attendance devices.</p>
                )}
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" placeholder="Worker contact email" {...register('email')} aria-invalid={!!errors.email} />
                  {errors.email ? <p className="text-sm text-destructive">{errors.email.message}</p> : null}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input id="phone" placeholder="Worker contact phone" {...register('phone')} />
                </div>
              </div>

              <div className="space-y-2 rounded-xl border border-border/70 bg-muted/20 p-4">
                <div>
                  <p className="text-sm font-medium text-foreground">Permanent address</p>
                  <p className="text-xs text-muted-foreground">Search an address to set the worker profile location. Coordinates are saved automatically from the map result.</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="worker-permanent-location-search">Search address</Label>
                  <Input
                    id="worker-permanent-location-search"
                    placeholder="Search for an address or place"
                    value={permanentLocationSearch}
                    onChange={(event) => setPermanentLocationSearch(event.target.value)}
                  />
                  {isSearchingPermanentLocation ? (
                    <p className="text-xs text-muted-foreground">Searching map locations...</p>
                  ) : null}
                  {!isSearchingPermanentLocation && permanentLocationSearch.trim().length >= 3 && permanentLocationOptions.length === 0 ? (
                    <p className="text-xs text-muted-foreground">No matching locations found.</p>
                  ) : null}
                  {permanentLocationOptions.length > 0 ? (
                    <div className="max-h-44 overflow-y-auto rounded-xl border border-border/60 bg-background">
                      {permanentLocationOptions.map((option) => (
                        <button
                          key={option.id}
                          type="button"
                          className="block w-full border-b border-border/40 px-3 py-2 text-left text-sm last:border-b-0 hover:bg-muted/60"
                          onClick={() => selectPermanentLocation(option)}
                        >
                          <span className="block font-medium text-foreground">{option.label}</span>
                          <span className="block text-xs text-muted-foreground">{option.details}</span>
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="address">Address</Label>
                  <Input id="address" placeholder="Permanent home or office address" {...register('address')} aria-invalid={!!errors.address} />
                </div>
                <input type="hidden" {...register('permanent_location_text')} />
                <input type="hidden" {...register('permanent_latitude')} />
                <input type="hidden" {...register('permanent_longitude')} />
                <p className="text-xs text-muted-foreground">
                  Selected permanent location: {permanentAddressValue || 'Not set'}
                </p>
              </div>

              <div className="space-y-2 rounded-xl border border-border/70 bg-muted/20 p-4">
                <div>
                  <p className="text-sm font-medium text-foreground">Linked address (UsrAddress)</p>
                  <p className="text-xs text-muted-foreground">This updates the linked usr_addrs record used for user address details.</p>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="linked_address_name">Name</Label>
                    <Input id="linked_address_name" placeholder="Address contact name" {...register('linked_address_name')} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="linked_address_door_no">Door no</Label>
                    <Input id="linked_address_door_no" placeholder="Door / house number" {...register('linked_address_door_no')} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="linked_address_building_name">Building name</Label>
                    <Input id="linked_address_building_name" placeholder="Building / apartment" {...register('linked_address_building_name')} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="linked_address_floor_line">Floor line</Label>
                    <Input id="linked_address_floor_line" placeholder="Floor / unit" {...register('linked_address_floor_line')} />
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label htmlFor="linked_address_street_line">Street line</Label>
                    <Input id="linked_address_street_line" placeholder="Street and area" {...register('linked_address_street_line')} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="linked_address_city_name">City</Label>
                    <Input id="linked_address_city_name" placeholder="City" {...register('linked_address_city_name')} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="linked_address_zipcode">Zip code</Label>
                    <Input id="linked_address_zipcode" placeholder="Postal code" {...register('linked_address_zipcode')} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="linked_address_mobile_number">Mobile number</Label>
                    <Input id="linked_address_mobile_number" placeholder="Address mobile number" {...register('linked_address_mobile_number')} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="linked_address_email">Email</Label>
                    <Input id="linked_address_email" type="email" placeholder="Address email" {...register('linked_address_email')} aria-invalid={!!errors.linked_address_email} />
                    {errors.linked_address_email ? <p className="text-sm text-destructive">{errors.linked_address_email.message}</p> : null}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Categories</CardTitle>
                <CardDescription>Select the job categories this worker prefers.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                <Controller
                  control={control}
                  name="categories"
                  render={({ field }) => (
                    <div className="max-h-56 space-y-1 overflow-auto rounded-xl border border-border/70 p-3">
                      {categoriesQuery.isLoading ? (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Spinner className="h-4 w-4" />
                          Loading categories...
                        </div>
                      ) : categoriesQuery.isError ? (
                        <p className="text-sm text-destructive">{getErrorMessage(categoriesQuery.error)}</p>
                      ) : categories.length > 0 ? (
                        categories.map((cat) => {
                          const categoryId = normalizeCategoryId(cat.id)
                          const selectedIds = (field.value ?? []).map(normalizeCategoryId)
                          const isChecked = selectedIds.includes(categoryId)
                          return (
                            <label
                              key={categoryId}
                              htmlFor={`worker-category-${categoryId}`}
                              className="flex cursor-pointer items-center gap-2 rounded-md px-1 py-1 hover:bg-muted/50"
                            >
                              <Checkbox
                                id={`worker-category-${categoryId}`}
                                checked={isChecked}
                                onCheckedChange={(checked) => {
                                  field.onChange(toggleCategory(field.value, categoryId, checked === true))
                                }}
                              />
                              <span className="text-sm text-foreground">{cat.name}</span>
                            </label>
                          )
                        })
                      ) : (
                        <p className="text-sm text-muted-foreground">No categories available for this service branch.</p>
                      )}
                    </div>
                  )}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">State</CardTitle>
                <CardDescription>Control worker approval and onboarding state.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <label className="flex items-center gap-3 rounded-xl border border-border/70 p-4">
                  <Checkbox checked={isVerified} onCheckedChange={(checked) => handleVerifiedChange(checked === true)} />
                  <div>
                    <p className="font-medium text-foreground">Verified</p>
                    <p className="text-sm text-muted-foreground">Mark the worker as verified if approval and onboarding are complete.</p>
                  </div>
                </label>
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate(returnPath)}>
            Cancel
          </Button>
          <Button type="submit" disabled={updateMutation.isPending}>
            {updateMutation.isPending ? (
              <>
                <Spinner className="mr-2" />
                Saving...
              </>
            ) : 'Save Changes'}
          </Button>
        </div>
      </form>
    </div>
  )
}
